import { EventEmitter, inject, Injector, TemplateRef, } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import cloneDeep from 'lodash.clonedeep';
import { debounceTime, distinctUntilChanged, filter, map, of, Subject, switchMap, tap } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { setFolderableOperation } from 'FoldersApp/state/folderable-operation/folderable-operation.action';
import { FOLDERABLE_OPERATION_SLICE_KEY } from 'FoldersApp/state/folderable-operation/folderable-operation.reducer';
import { FOLDERABLE_APP_CONFIG } from 'FoldersApp/constants/folderable-app-config.constants';
import { AccountsService } from 'AccountsApp/ts/services/accounts.service';
import { FolderableCollectionLoadOnScrollServiceBase } from 'FoldersApp/services/folderable-collection-load-on-scroll-service-base.service';
import { SelectedFolderableService } from 'FoldersApp/services/selected-folderable.service';
import * as i0 from "@angular/core";
import * as i1 from "AccountsApp/ts/services/accounts.service";
import * as i2 from "FoldersApp/services/folderable-collection-load-on-scroll-service-base.service";
import * as i3 from "FoldersApp/services/selected-folderable.service";
import * as i4 from "@ngrx/store";
/**
 * Base class for a modal component that renders a list of folderable items in collapsable folders.
 * Items can be sorted by sortable column headers.
 */
export class FolderableSavedModalBaseDirective {
    constructor(accountsService, folderableAppConfig, folderableCollectionService, selectedFolderableService, store) {
        this.accountsService = accountsService;
        this.folderableAppConfig = folderableAppConfig;
        this.folderableCollectionService = folderableCollectionService;
        this.selectedFolderableService = selectedFolderableService;
        this.store = store;
        this.cancelHandler = new EventEmitter();
        this.backHandler = new EventEmitter();
        this.maxLength = 255;
        this.showBackButton = false;
        this.isMultiSelect = false;
        this.selectedItems = [];
        this.searchTermInput = new FormControl('', { nonNullable: true });
        this.injector = inject(Injector);
        this.unsubscribe = new Subject();
        this.isOpen = false;
    }
    ngOnInit() {
        this.store
            .select(FOLDERABLE_OPERATION_SLICE_KEY)
            .pipe(takeUntil(this.unsubscribe), filter((operation) => operation === this.folderableOperationValue), tap(() => {
            this.isOpen = true;
        }), switchMap(() => {
            const selection = this.selectedFolderableService.getSelection();
            // If this is the first time the modal is being opened, initialize the params with
            // the user's filter preferences.
            if (this.folderableCollectionService.dataParams === null) {
                return this.accountsService.getUserPreferences().pipe(map((preferences) => {
                    const userPreferenceKey = this.userPreferenceFilterKey;
                    const preferenceParams = preferences[userPreferenceKey];
                    return {
                        ...preferenceParams,
                        viewRootFolder: selection?.ancestorFolderId || null,
                    };
                }));
            }
            // If the params are already initialized, use existing params.
            // Override the `viewRootFolder` param to open the modal in the selected item's parent folder.
            const params = {
                ...cloneDeep(this.folderableCollectionService.dataParams),
                viewRootFolder: selection?.ancestorFolderId || null,
            };
            return of(params);
        }))
            .subscribe((params) => {
            this.searchTermInput.setValue(params.name || '', { emitEvent: false });
            this.folderableCollectionService.refreshData(params, true);
        });
        this.searchTermInput.valueChanges
            .pipe(takeUntil(this.unsubscribe), debounceTime(300), distinctUntilChanged())
            .subscribe((value) => {
            const params = cloneDeep(this.folderableCollectionService.dataParams);
            params.name = value;
            this.folderableCollectionService.refreshData(params);
        });
    }
    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
        this.store.dispatch(setFolderableOperation({ operation: null }));
    }
    cancel() {
        this.close();
        this.cancelHandler.emit();
    }
    back() {
        this.close();
        this.backHandler.emit();
    }
    close() {
        this.isOpen = false;
        this.store.dispatch(setFolderableOperation({ operation: null }));
    }
    submit() {
        this.close();
    }
    setSort(sort) {
        const params = cloneDeep(this.folderableCollectionService.dataParams);
        params.sortColumn = sort.sortColumn;
        params.sortDirection = sort.sortDirection;
        this.folderableCollectionService.refreshData(params);
    }
    clearFilters() {
        const params = {
            ...cloneDeep(this.folderableCollectionService.dataParams),
            ...this.filterDefault,
        };
        this.searchTermInput.setValue(params.name || '', { emitEvent: false });
        this.folderableCollectionService.refreshData(params);
    }
    trackFolderable(folderable) {
        const { resourceType, item } = folderable;
        let unique_id;
        if ('id' in item) {
            unique_id = item.id;
        }
        else if ('name' in item) {
            unique_id = item.name;
        }
        else {
            unique_id = btoa(JSON.stringify(item));
        }
        return `${resourceType}-${unique_id}`;
    }
}
FolderableSavedModalBaseDirective.ɵfac = function FolderableSavedModalBaseDirective_Factory(__ngFactoryType__) { return new (__ngFactoryType__ || FolderableSavedModalBaseDirective)(i0.ɵɵdirectiveInject(i1.AccountsService), i0.ɵɵdirectiveInject(FOLDERABLE_APP_CONFIG), i0.ɵɵdirectiveInject(i2.FolderableCollectionLoadOnScrollServiceBase), i0.ɵɵdirectiveInject(i3.SelectedFolderableService), i0.ɵɵdirectiveInject(i4.Store)); };
FolderableSavedModalBaseDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: FolderableSavedModalBaseDirective, inputs: { modalHeader: "modalHeader", columnConfig: "columnConfig", sortableColumns: "sortableColumns", userPreferenceFilterKey: "userPreferenceFilterKey", filterDefault: "filterDefault", publicSharing: "publicSharing", maxLength: "maxLength", listItemFragment: "listItemFragment", columnHeaderTrailingFragment: "columnHeaderTrailingFragment", userHasNoDataMessage: "userHasNoDataMessage", loadingMessage: "loadingMessage", loadingTestId: "loadingTestId", showBackButton: "showBackButton" }, outputs: { cancelHandler: "cancelHandler", backHandler: "backHandler" } });
